/**
 * BL-019 Data Health Surfaces (2026-06-02)
 * ----------------------------------------
 * Two read-only sections that replaced the silent auto-flips removed from
 * `utils/deal_calculations.calculate_deal_metrics`:
 *
 *   1. TerminalStatusMismatchSection — deals where `terminal_locked=True`
 *      but `balance_with_fees > 0.01` (math has drifted from operator-
 *      declared terminal status). Operator reconciles by adjusting
 *      transactions OR clicking through to the deal to invoke `/reopen`.
 *
 *   2. DefaultWriteoffReclassifySection — Default deals with sum(WRITE_OFF)
 *      > 0 and balance_with_fees ≤ 0.01. Operator decides reclassify-or-not.
 *
 * Both fetch their backing endpoints lazily and offer "open deal" links;
 * neither mutates state.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Loader2, RefreshCw, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

export const TerminalStatusMismatchSection = () => {
  const { getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/data-health/terminal-status-mismatches`, { headers: getAuthHeaders() });
      setRows(r.data?.deals || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="dh-terminal-mismatch-section" className="border-amber-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" />
            Status Mismatch — Locked Terminal Deals with Residual Balance
            <Badge variant="outline" className="ml-2" data-testid="dh-terminal-mismatch-count">{rows.length}</Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="dh-terminal-mismatch-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          These deals are <code className="px-1 bg-slate-100 rounded">terminal_locked=True</code> (operator-declared PIF / Settled / Refinanced) but their effective balance has drifted above 1¢. Either reconcile the residual via transactions, or reopen the deal explicitly to allow status changes.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">{err}</div>
        )}
        {rows.length === 0 && !loading && !err && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="dh-terminal-mismatch-empty">
            No status mismatches detected. All locked terminal deals have zero (or sub-1¢) effective balance.
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="py-2 pr-4">Deal</th>
                  <th className="py-2 pr-4">Merchant</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Effective Balance</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`dh-terminal-mismatch-row-${r.deal_id}`}>
                    <td className="py-2 pr-4 font-mono">#{r.deal_id}</td>
                    <td className="py-2 pr-4">{r.merchant_name || r.deal_name || '—'}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">{r.status}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-amber-800">{fmtMoney(r.balance_with_fees)}</td>
                    <td className="py-2 pr-4">
                      <Link
                        to={`/deals/${r.id}`}
                        className="inline-flex items-center text-blue-600 hover:underline text-xs"
                        data-testid={`dh-terminal-mismatch-open-${r.deal_id}`}
                      >
                        Open <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export const DefaultWriteoffReclassifySection = () => {
  const { getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/data-health/default-writeoff-reclassify-prompts`, { headers: getAuthHeaders() });
      setRows(r.data?.deals || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="dh-default-writeoff-section" className="border-indigo-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-indigo-600" />
            Reclassify Prompts — Default Deals with Write-Off and Zero Balance
            <Badge variant="outline" className="ml-2" data-testid="dh-default-writeoff-count">{rows.length}</Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="dh-default-writeoff-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          These Default deals have been fully resolved by write-off (effective balance ≤ 1¢) but the operator has not yet reclassified them. Open each deal to settle / write-off via the standard flow — the system no longer auto-flips Default → Settled.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">{err}</div>
        )}
        {rows.length === 0 && !loading && !err && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="dh-default-writeoff-empty">
            No reclassify prompts pending. All written-off Default deals have been reclassified.
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="py-2 pr-4">Deal</th>
                  <th className="py-2 pr-4">Merchant</th>
                  <th className="py-2 pr-4 text-right">Write-Off Total</th>
                  <th className="py-2 pr-4 text-right">Effective Balance</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`dh-default-writeoff-row-${r.deal_id}`}>
                    <td className="py-2 pr-4 font-mono">#{r.deal_id}</td>
                    <td className="py-2 pr-4">{r.merchant_name || r.deal_name || '—'}</td>
                    <td className="py-2 pr-4 text-right font-mono">{fmtMoney(r.writeoff_total)}</td>
                    <td className="py-2 pr-4 text-right font-mono">{fmtMoney(r.balance_with_fees)}</td>
                    <td className="py-2 pr-4">
                      <Link
                        to={`/deals/${r.id}`}
                        className="inline-flex items-center text-blue-600 hover:underline text-xs"
                        data-testid={`dh-default-writeoff-open-${r.deal_id}`}
                      >
                        Reclassify <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default { TerminalStatusMismatchSection, DefaultWriteoffReclassifySection };
